import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './TakeQuiz.css';

const TakeQuiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if user is instructor/admin (preview only)
  const isInstructor = user?.role === 'instructor' || user?.role === 'admin' || user?.role === 'sub-admin';

  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Quiz state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Results state
  const [result, setResult] = useState(null);
  const [attemptInfo, setAttemptInfo] = useState(null);

  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        // Instructors get full quiz data, students get public data
        const quizEndpoint = isInstructor ? `/quizzes/${quizId}/full` : `/quizzes/${quizId}`;
        const quizRes = await api.get(quizEndpoint);
        setQuiz(quizRes.data);

        // Only fetch attempt info for students
        if (!isInstructor) {
          const attemptsRes = await api.get(`/quizzes/${quizId}/remaining-attempts`);
          setAttemptInfo(attemptsRes.data);
        }

        if (quizRes.data.time_limit) {
          setTimeRemaining(quizRes.data.time_limit);
        }
      } catch (err) {
        console.error('Error loading quiz:', err);
        setError('Failed to load quiz');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizData();
  }, [quizId, isInstructor]);

  // Timer effect
  useEffect(() => {
    if (!quizStarted || timeRemaining === null || result) return;

    if (timeRemaining <= 0) {
      handleSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [quizStarted, timeRemaining, result]);

  const handleSubmit = useCallback(async () => {
    if (submitting || result) return;
    setSubmitting(true);

    try {
      const startTime = quiz.time_limit ? quiz.time_limit - (timeRemaining || 0) : 0;

      const response = await api.post(`/quizzes/${quizId}/attempt`, {
        answers,
        time_taken: startTime,
      });

      setResult(response.data);
      setAttemptInfo((prev) => ({
        ...prev,
        attempts_used: prev.attempts_used + 1,
        remaining_attempts: response.data.remaining_attempts,
        best_score: Math.max(prev.best_score || 0, response.data.percentage),
        has_passed: prev.has_passed || response.data.passed,
      }));
    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError(err.response?.data?.error || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  }, [quizId, answers, timeRemaining, quiz, submitting, result]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startQuiz = () => {
    setQuizStarted(true);
  };

  const retakeQuiz = () => {
    setResult(null);
    setAnswers({});
    setCurrentQuestion(0);
    setQuizStarted(false);
    if (quiz.time_limit) {
      setTimeRemaining(quiz.time_limit);
    }
  };

  if (loading) {
    return (
      <div className="page take-quiz-page">
        <div className="container">
          <div className="loading-state">Loading quiz...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page take-quiz-page">
        <div className="container">
          <div className="error-state">
            <h2>Error</h2>
            <p>{error}</p>
            <button onClick={() => navigate(-1)} className="btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show result screen
  if (result) {
    return (
      <div className="page take-quiz-page">
        <div className="container">
          <div className="quiz-result">
            <div className={`result-header ${result.passed ? 'passed' : 'failed'}`}>
              <div className="result-icon">{result.passed ? '🎉' : '😔'}</div>
              <h1>{result.passed ? 'Congratulations!' : 'Keep Trying!'}</h1>
              <p className="result-subtitle">
                {result.passed
                  ? 'You passed the quiz!'
                  : `You need ${quiz.passing_score}% to pass.`}
              </p>
            </div>

            <div className="result-stats">
              <div className="stat-card">
                <span className="stat-label">Your Score</span>
                <span className="stat-value">{Math.round(result.percentage)}%</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Points</span>
                <span className="stat-value">
                  {result.score}/{result.max_score}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Passing Score</span>
                <span className="stat-value">{result.passing_score}%</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Attempts Left</span>
                <span className="stat-value">{result.remaining_attempts}</span>
              </div>
            </div>

            {result.certificate && (
              <div className="certificate-earned">
                <div className="certificate-icon">🏆</div>
                <h3>Certificate Earned!</h3>
                <p>Certificate #{result.certificate.certificate_number}</p>
                <Link to="/certificates" className="btn-primary">
                  View Certificate
                </Link>
              </div>
            )}

            {result.feedback && (
              <div className="result-feedback">
                <h3>Question Review</h3>
                {result.feedback.map((item, index) => {
                  // Parse options if stored as JSON string
                  const questionOptions = quiz.questions[index]?.options;
                  const options = typeof questionOptions === 'string'
                    ? JSON.parse(questionOptions)
                    : questionOptions;

                  const getUserAnswerText = () => {
                    if (item.user_answer === undefined || item.user_answer === null) {
                      return 'Not answered';
                    }
                    if (item.question_type === 'true_false') {
                      return item.user_answer === 0 ? 'True' : 'False';
                    }
                    return options?.[item.user_answer] || `Option ${item.user_answer + 1}`;
                  };

                  const getCorrectAnswerText = () => {
                    if (item.correct_answer === undefined || item.correct_answer === null) {
                      return '';
                    }
                    if (item.question_type === 'true_false') {
                      return item.correct_answer === 0 ? 'True' : 'False';
                    }
                    return options?.[item.correct_answer] || `Option ${item.correct_answer + 1}`;
                  };

                  return (
                    <div
                      key={item.question_id}
                      className={`feedback-item ${item.is_correct ? 'correct' : 'incorrect'}`}
                    >
                      <div className="feedback-header">
                        <span className="question-num">Question {index + 1}</span>
                        <span className={`feedback-status ${item.is_correct ? 'correct' : 'incorrect'}`}>
                          {item.is_correct ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                      </div>
                      <p className="feedback-question">{item.question}</p>
                      <div className="feedback-answers">
                        <p>
                          <strong>Your answer:</strong> {getUserAnswerText()}
                        </p>
                        {!item.is_correct && item.correct_answer !== null && (
                          <p>
                            <strong>Correct answer:</strong> {getCorrectAnswerText()}
                          </p>
                        )}
                      </div>
                      {item.explanation && (
                        <div className="feedback-explanation">
                          <strong>Explanation:</strong> {item.explanation}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="result-actions">
              {result.remaining_attempts > 0 && !result.passed && (
                <button onClick={retakeQuiz} className="btn-primary">
                  Retake Quiz
                </button>
              )}
              <button onClick={() => navigate(-1)} className="btn-secondary">
                Back to Course
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show start screen
  if (!quizStarted) {
    return (
      <div className="page take-quiz-page">
        <div className="container">
          <div className="quiz-start">
            <div className="start-header">
              <h1>{quiz.title}</h1>
              {quiz.description && <p className="quiz-description">{quiz.description}</p>}
            </div>

            <div className="quiz-info-grid">
              <div className="info-card">
                <span className="info-icon">📝</span>
                <span className="info-label">Questions</span>
                <span className="info-value">{quiz.questions?.length || 0}</span>
              </div>
              <div className="info-card">
                <span className="info-icon">🎯</span>
                <span className="info-label">Passing Score</span>
                <span className="info-value">{quiz.passing_score}%</span>
              </div>
              {quiz.time_limit && (
                <div className="info-card">
                  <span className="info-icon">⏱️</span>
                  <span className="info-label">Time Limit</span>
                  <span className="info-value">{formatTime(quiz.time_limit)}</span>
                </div>
              )}
              {!isInstructor && (
                <div className="info-card">
                  <span className="info-icon">🔄</span>
                  <span className="info-label">Attempts Left</span>
                  <span className="info-value">
                    {attemptInfo?.remaining_attempts || 0}/{attemptInfo?.max_attempts || 3}
                  </span>
                </div>
              )}
            </div>

            {/* Instructor Preview Mode */}
            {isInstructor ? (
              <div className="instructor-preview-notice">
                <div className="preview-icon">👁️</div>
                <h3>Preview Mode</h3>
                <p>As an instructor, you can preview the quiz questions but cannot submit attempts.</p>
                <div className="preview-actions">
                  <button onClick={startQuiz} className="btn-start">
                    Preview Questions
                  </button>
                  <button onClick={() => navigate(`/quiz/${quizId}/manage`)} className="btn-secondary">
                    Edit Quiz
                  </button>
                  <button onClick={() => navigate(-1)} className="btn-secondary">
                    Back to Course
                  </button>
                </div>
              </div>
            ) : (
              <>
                {attemptInfo?.has_passed && (
                  <div className="already-passed">
                    <span className="passed-icon">✓</span>
                    <span>You've already passed this quiz with {Math.round(attemptInfo.best_score)}%</span>
                  </div>
                )}

                {attemptInfo?.can_attempt ? (
                  <button onClick={startQuiz} className="btn-start">
                    Start Quiz
                  </button>
                ) : (
                  <div className="no-attempts">
                    <p>You have no remaining attempts for this quiz.</p>
                    <button onClick={() => navigate(-1)} className="btn-secondary">
                      Back to Course
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Quiz in progress
  const questions = quiz.questions || [];
  const currentQ = questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;

  return (
    <div className="page take-quiz-page">
      <div className="container">
        <div className="quiz-container">
          {/* Header */}
          <div className="quiz-header">
            <h2>{quiz.title}</h2>
            <div className="header-info">
              {timeRemaining !== null && (
                <div className={`timer ${timeRemaining < 60 ? 'warning' : ''}`}>
                  <span className="timer-icon">⏱️</span>
                  <span>{formatTime(timeRemaining)}</span>
                </div>
              )}
              <div className="progress-info">
                {answeredCount}/{questions.length} answered
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>

          {/* Question navigation */}
          <div className="question-nav">
            {questions.map((q, index) => (
              <button
                key={q.id}
                className={`nav-dot ${index === currentQuestion ? 'active' : ''} ${
                  answers[q.id] !== undefined ? 'answered' : ''
                }`}
                onClick={() => setCurrentQuestion(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {/* Question */}
          {currentQ && (
            <div className="question-container">
              <div className="question-header">
                <span className="question-number">
                  Question {currentQuestion + 1} of {questions.length}
                </span>
                <span className="question-points">{currentQ.points} point(s)</span>
              </div>

              <h3 className="question-text">{currentQ.question}</h3>

              <div className="options-container">
                {currentQ.question_type === 'true_false' ? (
                  <>
                    <label
                      className={`option-label ${answers[currentQ.id] === 0 ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQ.id}`}
                        checked={answers[currentQ.id] === 0}
                        onChange={() => handleAnswerChange(currentQ.id, 0)}
                      />
                      <span className="option-text">True</span>
                    </label>
                    <label
                      className={`option-label ${answers[currentQ.id] === 1 ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQ.id}`}
                        checked={answers[currentQ.id] === 1}
                        onChange={() => handleAnswerChange(currentQ.id, 1)}
                      />
                      <span className="option-text">False</span>
                    </label>
                  </>
                ) : (
                  // Multiple choice
                  (typeof currentQ.options === 'string'
                    ? JSON.parse(currentQ.options)
                    : currentQ.options
                  )?.map((option, index) => (
                    <label
                      key={index}
                      className={`option-label ${answers[currentQ.id] === index ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQ.id}`}
                        checked={answers[currentQ.id] === index}
                        onChange={() => handleAnswerChange(currentQ.id, index)}
                      />
                      <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                      <span className="option-text">{option}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="quiz-actions">
            <button
              className="btn-nav"
              onClick={() => setCurrentQuestion((prev) => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0}
            >
              Previous
            </button>

            {currentQuestion < questions.length - 1 ? (
              <button
                className="btn-nav btn-next"
                onClick={() => setCurrentQuestion((prev) => prev + 1)}
              >
                Next
              </button>
            ) : isInstructor ? (
              <button
                className="btn-nav btn-next"
                onClick={() => navigate(-1)}
              >
                End Preview
              </button>
            ) : (
              <button
                className="btn-submit"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Quiz'}
              </button>
            )}
          </div>

          {/* Preview mode indicator for instructors */}
          {isInstructor && (
            <div className="preview-banner">
              Preview Mode - Submissions disabled for instructors
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TakeQuiz;
